import { memo, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, Heading, Text } from '@zillow/constellation-10';
import { IconPlusCircleFilled } from '@zillow/constellation-icons';

import clickstreamUtils from '../../../../srcLegacy/shared/utils/clickstreamUtils';
import { useGetListingDetails } from '../../../hooks/queries/properties';

import { TRIGGER_LOCATIONS } from '../../../constants/ClickstreamConstants';

import InfoPopover from '../../../../srcLegacy/shared/components/WizardModularListing/SpecialOffers/components/InfoPopover';
import SpecialOffersModal from '../../modals/SpecialOffersModal';

import { emitSpecialOffersAddNewEvent } from '../../../analytics/events/specialOffers';
import { useAuth } from '../../AuthProvider';
import SpecialOfferItem from './SpecialOfferItem';

type Props = {
  isRelistingFieldset?: boolean;
};

const SpecialOffers = ({ isRelistingFieldset = false }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const specialOffers = listingDetails?.specialOffers ?? [];
  const isFeed = listingDetails?.isFeed ?? false;

  const heading = specialOffers.length > 0 ? 'Current special offer' : 'Do you want to promote a special offer?';
  const [showAddModal, setShowAddModal] = useState(false);
  const [firstClickToAddOffer, setFirstClickToAddOffer] = useState(false);

  const triggerLocation = isRelistingFieldset ? TRIGGER_LOCATIONS.POSTING_PATH_REVIEW : TRIGGER_LOCATIONS.POSTING_PATH;

  useEffect(() => {
    if (firstClickToAddOffer && user && listingDetails && specialOffers.length === 0) {
      const suffix = clickstreamUtils.getListingStatusSuffix(
        listingDetails.active,
        listingDetails.lastActivatedMs?.toString() ?? '',
      );
      emitSpecialOffersAddNewEvent({
        currentProperty: listingDetails,
        user,
        triggerLocation: `${triggerLocation}${suffix}`,
      });
      setFirstClickToAddOffer(false);
    }
  }, [firstClickToAddOffer, user, listingDetails, specialOffers.length, triggerLocation]);

  const handleAddClick = () => {
    if (specialOffers.length === 0) {
      setFirstClickToAddOffer(true);
    }
    setShowAddModal(true);
  };

  return (
    <Box data-testid="SpecialOffers" css={{ marginBottom: '1200' }}>
      {isRelistingFieldset ? (
        <Box css={{ display: 'flex', flexDirection: 'row', marginBottom: '100' }}>
          <Text textStyle="body-sm-bold" asChild>
            <p>{heading}</p>
          </Text>
          {listingDetails && user && (
            <InfoPopover
              currentProperty={listingDetails}
              user={user}
              triggerLocation={TRIGGER_LOCATIONS.POSTING_PATH_REVIEW}
            />
          )}
        </Box>
      ) : (
        <Box css={{ display: 'flex', flexDirection: 'row', marginTop: { base: 0, lg: '600' }, marginBottom: '800' }}>
          <Heading level={3} textStyle="heading-sm">
            {heading}
          </Heading>
          {listingDetails && user && (
            <InfoPopover
              currentProperty={listingDetails}
              user={user}
              triggerLocation={TRIGGER_LOCATIONS.POSTING_PATH}
            />
          )}
        </Box>
      )}

      {specialOffers.length <= 0 ? (
        <Button
          onClick={handleAddClick}
          tone="brand"
          emphasis="outlined"
          size="md"
          css={{ marginRight: '800' }}
          icon={<IconPlusCircleFilled />}
        >
          Add an offer
        </Button>
      ) : (
        <Box css={{ marginTop: isRelistingFieldset ? '200' : '800' }}>
          <SpecialOfferItem
            isInPostingPath={true}
            isFeed={isFeed}
            listingAlias={listingAlias}
            specialOffer={specialOffers[0]}
          />
        </Box>
      )}

      {showAddModal && <SpecialOffersModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} />}
    </Box>
  );
};

export default memo(SpecialOffers);
